import { useState } from 'react'

/*
  Select — the one dropdown in the app.

  It wraps a native <select> on purpose: the OS picker is the most accessible,
  keyboard-friendly, and mobile-correct control there is, and reinventing it
  (the "custom listbox" reflex) only loses those for flavour. What the native
  element gets wrong is *looks*: every browser draws a different caret, and the
  app's old call sites each killed the focus ring with `focus:outline-none`
  without replacing it, so keyboard focus was invisible. This component fixes
  both once: `appearance: none` drops the native caret for a single chevron, and
  focus is shown by an accent border (plus the global :focus-visible ring, which
  we no longer suppress) so tab-navigation is always visible.

  Sizes match the density tiers already in use: `xs` for inline rule-builder
  rows, `sm` for compact toolbars, `md` for forms.
*/

type SelectSize = 'xs' | 'sm' | 'md'

const SIZES: Record<SelectSize, { font: number; padY: number; padX: number; caret: number; radius: number }> = {
  xs: { font: 12, padY: 4, padX: 8, caret: 7, radius: 4 },
  sm: { font: 13, padY: 6, padX: 10, caret: 9, radius: 6 },
  md: { font: 14, padY: 8, padX: 12, caret: 11, radius: 8 },
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'style'> {
  size?: SelectSize
  /** Round to a pill (the Stream endpoint filter). */
  pill?: boolean
  /** Stretch to the container width (forms). */
  fullWidth?: boolean
  /** Layout styles land on the wrapper, not the inner control. */
  style?: React.CSSProperties
  /** Layout classes (flex-1, min-w-…) land on the wrapper. */
  className?: string
}

export function Select({
  size = 'md',
  pill = false,
  fullWidth = false,
  className,
  style,
  disabled,
  children,
  ...selectProps
}: SelectProps) {
  const [focused, setFocused] = useState(false)
  const s = SIZES[size]
  const radius = pill ? 999 : s.radius

  return (
    <span
      className={className}
      style={{
        position: 'relative',
        display: fullWidth ? 'block' : 'inline-block',
        width: fullWidth ? '100%' : undefined,
        minWidth: 0,
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      <select
        {...selectProps}
        disabled={disabled}
        onFocus={(e) => { setFocused(true); selectProps.onFocus?.(e) }}
        onBlur={(e) => { setFocused(false); selectProps.onBlur?.(e) }}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          width: '100%',
          minWidth: 0,
          // Right padding leaves room for the chevron; the rest follows the tier.
          padding: `${s.padY}px ${s.padX + s.caret + 10}px ${s.padY}px ${s.padX}px`,
          borderRadius: radius,
          fontSize: s.font,
          fontWeight: 500,
          fontFamily: 'inherit',
          lineHeight: 1.3,
          color: 'var(--text)',
          backgroundColor: 'var(--surface)',
          border: `1px solid ${focused ? 'var(--accent)' : 'var(--border)'}`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textOverflow: 'ellipsis',
          transition: 'border-color 140ms ease',
        }}
      >
        {children}
      </select>
      <svg
        aria-hidden="true"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.25}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          position: 'absolute',
          top: '50%',
          right: s.caret,
          transform: 'translateY(-50%)',
          color: 'var(--muted)',
          pointerEvents: 'none',
        }}
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </span>
  )
}
